import t from '@jetshop/intl';
import Drawer, { DrawerTarget, DrawerTrigger } from '@jetshop/ui/Modal/Drawer';
import React, { useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import FilterDrawer from './Mobile/FilterDrawer';
import ListSorting from './ListSorting';
import { BooleanFilters } from './BooleanFilters';
import { ListFilters } from './ListFilters';
import { theme } from '../../Theming/Theming';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH } from '@fortawesome/pro-light-svg-icons';
import { PagePadding } from '../../Layout/MaxWidth';
import { NumberBadge } from '../../ui/NumberBadge';
import { getFontSize } from '../../Theming/helpers';
import { RangeFilters } from './RangeFilters';
import { Above } from '@jetshop/ui/Breakpoints';
import { useFilters } from '@jetshop/core/hooks/Filters/useFilters';
import { useRef } from 'react';
import useClickOutside from '../../ui/hooks/useClickOutside';
import { ButtonUI } from '../../ui/ButtonUI';
import CloseButton from '../../ui/CloseButton';

const buttonCss = `
display: flex;
align-items: center;
width: 100%;
height: 100%;
letter-spacing: 0.9px;
font-weight: ${theme.button.fontWeight};
letter-spacing: ${theme.button.letterSpacing};
font-size: 0.9rem;
border: 1px solid ${theme.colors.border};
padding: 0 2rem 0 1rem;
text-transform: uppercase;
.icon-holder {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}
svg {
  font-size: 1.5rem;
}
`;

const FilterWrapper = styled('div')`
  margin: 1rem 0;
`;

const MobileButtons = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: ${theme.colors.white};
  height: 55px;
  ${theme.below.sm} {
    > p {
      display: none;
    }
  }
  > div,
  [data-flight-dropdown] {
    height: 100%;
    width: auto;
  }
  .mobile-filter-button-wrapper {
    > button {
      ${buttonCss}
      .number-badge {
        position: static;
        margin-left: 5px;
        background: ${theme.colors.primaryLight};
      }
      .title {
        margin-left: 5px;
      }
    }
  }
  [data-flight-dropdown] {
    [data-flight-dropdown-button] {
      ${buttonCss}
      &[aria-expanded='true'] {
        border-bottom: 1px solid ${theme.colors.white};
      }
      .title {
        margin-right: 5px;
      }
    }
    [data-flight-dropdown-items] {
      border: 1px solid ${theme.colors.border};
      border-top: none;
      width: 100%;
      min-width: 210px;
      font-size: 0.9rem;
      [data-flight-dropdown-item]:focus {
        color: inherit;
      }
      [data-flight-dropdown-item]:first-child > div {
        border-top: 1px solid ${theme.colors.border};
      }
    }
  }
  &[data-scroll='true'] {
    position: fixed;
    height: 32px;
    padding: 0 ${theme.general.pagePadding.sm};
    top: ${theme.mobileHeader.height.scroll + 'px'};
    left: 0;
    width: 100%;
    z-index: 90;
    box-shadow: ${theme.general.boxShadow};
    .mobile-filter-button {
      padding: 0;
      background: none;
      color: ${theme.colors.font};
      border: none;
    }
    [data-flight-dropdown] {
      [data-flight-dropdown-button] {
        justify-content: flex-end;
        padding: 0;
        height: 32px;
        border: none;
        > svg {
          margin-left: 6px;
        }
      }
      [data-flight-dropdown-items] {
        top: 31px;
      }
    }
  }
`;

const DesktopFilterWrapper = styled('div')`
  position: relative;

  .close-filter {
    top: 1rem;
    right: 1rem;
    position: absolute;
    display: flex;
    align-items: center;
    > button:not(.close-button) {
      font-size: 0.8rem;
      padding: 0 1rem;
    }
    .close-button {
      margin-left: 5px;
    }
  }
  .clear-button-wrapper {
    top: 1rem;
    position: absolute;
  }
  .filters {
    display: flex;
    width: 100%;
    padding: 5rem 2rem;
    flex-wrap: wrap;
  }

  //FILTER ITEMS
  div.filter {
    width: 20%;
    padding: 0 2rem;
    margin-bottom: 2rem;
    border-left: 1px solid #eee;
  }

  .title {
    ${getFontSize(theme.categoryPage.filter.desktop.titles.fontSize)};
    font-family: ${theme.categoryPage.filter.desktop.titles.fontFamily};
    font-weight: ${theme.categoryPage.filter.desktop.titles.fontWeight};
    text-transform: ${theme.categoryPage.filter.desktop.titles.textTransform};
    letter-spacing: ${theme.categoryPage.filter.desktop.titles.letterSpacing};
    .filter-active-count {
      margin-left: 2px;
    }
  }

  .filter-items {
    max-height: 500px;
    overflow: auto;
    > li {
      margin: 10px 0;
      min-height: 25px;
      .input-box {
        margin-right: 10px;
      }
      .count {
        margin-left: 3px;
      }
    }
  }

  .range-slider-wrapper {
    padding: 1rem 0;
  }
`;

const ClearButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  button {
    width: 130px;
    font-size: 0.9rem;
    padding: 5px 10px;
    border: 1px solid ${theme.colors.border};
    height: 36px;
  }
`;

const ProductNumberText = styled('p')`
  color: #878787;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
`;

export const ProductNumber = ({ total }) => {
  return (
    <ProductNumberText>
      {t('{total, plural, =1 {# product} other {# products}}', {
        total,
      })}
    </ProductNumberText>
  );
};

const DesktopFilter = ({
  filters,
  activeFilters,
  scroll,
  setDesktopFilter,
}) => {
  const showResetButton = activeFilters > 0;
  const { clearAllFilters } = useFilters();
  const innerRef = useRef();
  useClickOutside({
    innerRef,
    close: () => setDesktopFilter(false),
  });
  return (
    <>
      <DesktopFilterWrapper>
        <div className="close-filter" onClick={() => setDesktopFilter(false)}>
          <ButtonUI className="list">{t('Show products')}</ButtonUI>
          <CloseButton className="close-button" />
        </div>
        {showResetButton && (
          <ClearButtonWrapper className="clear-button-wrapper">
            <button
              onClick={() => {
                clearAllFilters();
                setDesktopFilter(false);
              }}
            >
              {t('Clear all filters')}
            </button>
          </ClearButtonWrapper>
        )}
        <div className="filters">
          <ListFilters filters={filters} />
          <RangeFilters filters={filters} />
          <BooleanFilters filters={filters} showTitle={true} />
        </div>
      </DesktopFilterWrapper>
    </>
  );
};

const Filter = ({ filters, sortOrders, total }) => {
  const [scroll, setScroll] = useState(false);
  const [showDesktopFilter, setDesktopFilter] = useState(false);
  if (typeof window !== 'undefined' && window.scrollY === 0 && scroll)
    setScroll(false);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined')
        window.removeEventListener('scroll', handleScroll);
    };
  });

  const handleScroll = (event) => {
    if (document.getElementById('filter-wrapper')) {
      const filterTop =
        document.getElementById('filter-wrapper').offsetTop -
        document.getElementById('main-header').offsetHeight;
      if (window.scrollY < filterTop && scroll) {
        setScroll(false);
      } else if (window.scrollY > filterTop && !scroll) {
        setScroll(true);
      }
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', handleScroll);
  }

  const showFilter = filters && filters.length > 0;
  const showSorting = sortOrders && sortOrders.length > 0;

  const activeFilters = filters.filter(
    (item) => item.hasActiveItems || item.isActive || item.value
  ).length;

  if (!showFilter && !showSorting) return null;

  return (
    <FilterWrapper id="filter-wrapper">
      <PagePadding>
        <Above breakpoint="lg">
          {(matches) =>
            matches ? (
              <>
                <MobileButtons>
                  <div className="mobile-filter-button-wrapper">
                    {showFilter && (
                      <button
                        className="mobile-filter-button"
                        onClick={() => {
                          setDesktopFilter(!showDesktopFilter);
                        }}
                      >
                        <div className="icon-holder">
                          <FontAwesomeIcon icon={faSlidersH} />
                        </div>
                        <span className="filter-button-name title">
                          {t('Filter')}
                        </span>
                        {activeFilters > 0 && (
                          <NumberBadge text={activeFilters} />
                        )}
                      </button>
                    )}
                  </div>
                  <ProductNumber total={total} />
                  {showSorting && <ListSorting sortOrders={sortOrders} />}
                </MobileButtons>
                {showDesktopFilter && (
                  <DesktopFilter
                    {...{ filters, activeFilters, setDesktopFilter }}
                  />
                )}
              </>
            ) : (
              <>
                {scroll && <MobileButtons />}
                <MobileButtons data-scroll={scroll}>
                  <div className="mobile-filter-button-wrapper">
                    {showFilter && (
                      <DrawerTrigger
                        id="filterDrawer"
                        coverStyles={{ zIndex: 105 }}
                      >
                        {({ showTarget }) => (
                          <button
                            className="mobile-filter-button"
                            onClick={showTarget}
                          >
                            <div className="icon-holder">
                              <FontAwesomeIcon icon={faSlidersH} />
                            </div>
                            <span className="filter-button-name title">
                              {t('Filter')}
                            </span>
                            {activeFilters > 0 && (
                              <NumberBadge text={activeFilters} />
                            )}
                          </button>
                        )}
                      </DrawerTrigger>
                    )}
                  </div>
                  <ProductNumber total={total} />
                  {showSorting && <ListSorting sortOrders={sortOrders} />}
                </MobileButtons>
                {showFilter && (
                  <DrawerTarget id="filterDrawer">
                    {({ hideTarget, isOpen }) => (
                      <Drawer isOpen={isOpen} left>
                        <FilterDrawer close={hideTarget} filters={filters} />
                      </Drawer>
                    )}
                  </DrawerTarget>
                )}
              </>
            )
          }
        </Above>
      </PagePadding>
    </FilterWrapper>
  );
};

export default Filter;
